import React, { useState, useEffect, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  ImageList,
  ImageListItem,
  ListSubheader,
  LinearProgress,
  Paper,
  Divider,
} from "@material-ui/core";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import ImageIcon from "@material-ui/icons/Image";
import api from "../../../axios";
import Api from "../../../Api";
import { useAlert } from "react-alert";

const useStyles = makeStyles((theme) => ({
  section: {
    backgroundColor: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    overflow: "hidden",
    marginBottom: theme.spacing(3),
  },

  sectionHeader: {
    padding: theme.spacing(2),
    backgroundColor: "#fafafa",
  },

  sectionTitle: {
    fontWeight: 600,
  },

  imageList: {
    width: "100%",
    maxHeight: 450,
    padding: theme.spacing(2),
    margin: 0,
    overflowY: "auto",
  },

  imageItem: {
    overflow: "hidden",
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    backgroundColor: "#f5f5f5",
  },

  existingImg: {
    width: "100%",
    height: 200,
    objectFit: "cover",
    display: "block",
  },

  uploadSection: {
    padding: theme.spacing(3),
  },

  previewWrapper: {
    width: "100%",
    minHeight: 220,
    border: "1px dashed #d1d5db",
    borderRadius: 10,
    backgroundColor: "#f9fafb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },

  previewImg: {
    width: "100%",
    height: 220,
    objectFit: "contain",
    backgroundColor: "#f3f4f6",
  },

  emptyPreview: {
    minHeight: 220,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    color: "#9ca3af",
  },

  fileInput: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
    },
  },

  uploadButton: {
    height: 56,
    fontWeight: 600,
  },

  helperText: {
    marginTop: theme.spacing(0.5),
    display: "block",
  },

  progressBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: "#f9fafb",
    borderRadius: 8,
  },

  noRecord: {
    padding: theme.spacing(3),
    textAlign: "center",
    color: "#9ca3af",
  },
}));

function LinearProgressWithLabel(props) {
  return (
    <Box display="flex" alignItems="center">
      <Box width="100%" mr={1}>
        <LinearProgress variant="determinate" {...props} />
      </Box>

      <Box minWidth={40}>
        <Typography variant="body2" color="textSecondary">
          {`${Math.round(props.value)}%`}
        </Typography>
      </Box>
    </Box>
  );
}

export default function TitlebarImageList(props) {
  const classes = useStyles();
  const alert = useAlert();

  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [filesImage, setFilesImage] = useState([]);

  // =========================
  // PROGRESS BAR
  // =========================
  useEffect(() => {
    let timer;

    if (loading) {
      timer = setInterval(() => {
        setProgress((oldProgress) => {
          if (oldProgress >= 100) {
            return 100;
          }

          const diff = Math.random() * 10;

          return Math.min(oldProgress + diff, 95);
        });
      }, 500);
    } else {
      setProgress(0);
    }

    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [loading]);

  // =========================
  // PREVIEW URL
  // =========================
  const previewUrl = useMemo(() => {
    if (!filesImage.length) {
      return null;
    }

    return URL.createObjectURL(filesImage[0]);
  }, [filesImage]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // =========================
  // FILE CHANGE
  // =========================
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);

    setFilesImage(selectedFiles);
  };

  // =========================
  // UPLOAD
  // =========================
  const handleUploadClick = async (e) => {
    e.preventDefault();

    const itemDetails = props.ItemData?.data?.[0];

    if (!itemDetails) {
      return alert.error("Missing Item Data");
    }

    const validFiles = Array.from(filesImage).filter(
      (file) => file.size < 2000000
    );

    if (validFiles.length === 0 && filesImage.length > 0) {
      return alert.error("Selected images are too large (Max 2MB)");
    }

    if (validFiles.length === 0) {
      return alert.error("Please select at least one image");
    }

    setLoading(true);

    const promiseArray = validFiles.map((file) => {
      const formData = new FormData();

      formData.append(
        "privateSaleGroup",
        itemDetails.privateSalesGroup
      );

      formData.append("id", itemDetails.id);
      formData.append("isFeatured", 1);
      formData.append("filename", file.name);
      formData.append("files", file);

      return api.post(
        `${Api.request.URL}/private-sale/image/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
    });

    try {
      await Promise.all(promiseArray);

      setProgress(100);

      alert.success("Successfully Uploaded");

      setFilesImage([]);

      // Uncomment if you want automatic refresh:
      // window.location.reload();
    } catch (error) {
      console.error("Upload error:", error);

      alert.error(
        error.response?.data?.msg || "Upload failed"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* =========================
          CURRENT IMAGES
      ========================= */}
      <Paper className={classes.section} elevation={0}>
        <Box className={classes.sectionHeader}>
          <Box display="flex" alignItems="center">
            <ImageIcon
              color="primary"
              style={{ marginRight: 10 }}
            />

            <Box>
              <Typography
                variant="h6"
                className={classes.sectionTitle}
              >
                Current Images
              </Typography>

              <Typography variant="body2" color="textSecondary">
                Existing images for this item
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider />

        <ImageList
          rowHeight={200}
          className={classes.imageList}
          cols={2}
        >
          {props.ItemData?.image?.length > 0 ? (
            props.ItemData.image.map((item, i) => (
              <ImageListItem
                key={i}
                className={classes.imageItem}
              >
                <img
                  className={classes.existingImg}
                  src={item.img_url}
                  alt={`Item ${i + 1}`}
                />
              </ImageListItem>
            ))
          ) : (
            <Box className={classes.noRecord}>
              <Typography variant="body2">
                No images found
              </Typography>
            </Box>
          )}
        </ImageList>
      </Paper>

      {/* =========================
          UPLOAD NEW IMAGE
      ========================= */}
      <Paper className={classes.section} elevation={0}>
        <Box className={classes.sectionHeader}>
          <Box display="flex" alignItems="center">
            <CloudUploadIcon
              color="primary"
              style={{ marginRight: 10 }}
            />

            <Box>
              <Typography
                variant="h6"
                className={classes.sectionTitle}
              >
                Upload New Image
              </Typography>

              <Typography variant="body2" color="textSecondary">
                Add new images to this item
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider />

        <Box className={classes.uploadSection}>
          {/* Preview */}
          <Box mb={3}>
            <Typography
              variant="subtitle2"
              gutterBottom
            >
              Image Preview
            </Typography>

            <Box className={classes.previewWrapper}>
              {previewUrl ? (
                <img
                  className={classes.previewImg}
                  src={previewUrl}
                  alt="Selected preview"
                />
              ) : (
                <Box className={classes.emptyPreview}>
                  <ImageIcon
                    style={{
                      fontSize: 48,
                      marginBottom: 8,
                    }}
                  />

                  <Typography
                    variant="body2"
                    color="textSecondary"
                  >
                    No image selected
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Progress */}
          {loading && (
            <Box className={classes.progressBox}>
              <LinearProgressWithLabel value={progress} />
            </Box>
          )}

          {/* Upload Controls */}
          <Grid
            container
            spacing={2}
            alignItems="center"
          >
            <Grid item md={8} xs={12}>
              <TextField
                fullWidth
                type="file"
                variant="outlined"
                className={classes.fileInput}
                inputProps={{
                  accept: "image/jpeg, image/png",
                  multiple: true,
                }}
                onChange={handleFileChange}
                disabled={loading}
              />

              <Typography
                variant="caption"
                color="textSecondary"
                className={classes.helperText}
              >
                JPG or PNG · Maximum 2MB per image
              </Typography>
            </Grid>

            <Grid item md={4} xs={12}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                className={classes.uploadButton}
                startIcon={<CloudUploadIcon />}
                disabled={
                  !filesImage.length || loading
                }
                onClick={handleUploadClick}
              >
                {loading ? "Uploading..." : "Upload Image"}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
}